<template>
  <div
    class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50"
  >
    <div
      class="relative top-20 mx-auto p-5 border w-11/12 md:w-2/3 lg:w-1/2 shadow-lg rounded-md bg-white max-h-screen overflow-y-auto"
    >
      <div class="mt-3">
        <!-- Header -->
        <div class="flex items-center justify-between mb-6">
          <h3 class="text-lg font-medium text-gray-900">New Leave Request</h3>
          <button @click="closeModal" class="text-gray-400 hover:text-gray-600">
            <X class="w-6 h-6" />
          </button>
        </div>

        <!-- Form -->
        <form @submit.prevent="submitRequest" class="space-y-6">
          <!-- Leave Type Selection -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-3">
              Leave Type *
            </label>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div
                v-for="type in availableLeaveTypes"
                :key="type.key"
                @click="selectLeaveType(type.key)"
                :class="[
                  'p-4 border-2 rounded-lg cursor-pointer transition-colors',
                  form.leaveType === type.key
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300',
                ]"
              >
                <div class="flex items-center">
                  <div
                    :class="[
                      'p-2 rounded-full mr-3',
                      getLeaveTypeBackgroundClass(type),
                    ]"
                  >
                    <component
                      :is="getLeaveTypeIcon(type.key)"
                      :class="['w-5 h-5', getLeaveTypeIconClass(type)]"
                    />
                  </div>
                  <div class="flex-1">
                    <p class="font-medium text-gray-900">{{ type.name }}</p>
                    <p class="text-sm text-gray-500">{{ type.description }}</p>
                    <p
                      v-if="type.balance !== undefined"
                      class="text-xs text-gray-600 mt-1"
                    >
                      {{ type.balance }} days remaining
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Short Leave Fields -->
          <div v-if="selectedLeaveTypeInfo?.isShortLeave || form.leaveType === 'short' || form.leaveType === 'short_leave'" class="space-y-4">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  Date *
                </label>
                <input
                  v-model="form.shortLeave.date"
                  type="date"
                  required
                  :min="getTodayDate()"
                  :max="getMaxShortLeaveDate()"
                  class="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  Start Time *
                </label>
                <input
                  v-model="form.shortLeave.startTime"
                  type="time"
                  required
                  class="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
            </div>

            <div>
              <label class="block text-sm font-medium text-gray-700 mb-2">
                Duration Type *
              </label>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                <div
                  @click="form.shortLeave.durationType = 'preset'"
                  :class="[
                    'p-3 border-2 rounded-lg cursor-pointer transition-colors',
                    form.shortLeave.durationType === 'preset'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300',
                  ]"
                >
                  <div class="flex items-center">
                    <Clock class="w-5 h-5 text-blue-600 mr-2" />
                    <div>
                      <p class="font-medium text-gray-900">Preset Duration</p>
                      <p class="text-sm text-gray-500">
                        Choose from common durations
                      </p>
                    </div>
                  </div>
                </div>
                <div
                  @click="form.shortLeave.durationType = 'custom'"
                  :class="[
                    'p-3 border-2 rounded-lg cursor-pointer transition-colors',
                    form.shortLeave.durationType === 'custom'
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-gray-300',
                  ]"
                >
                  <div class="flex items-center">
                    <FileText class="w-5 h-5 text-green-600 mr-2" />
                    <div>
                      <p class="font-medium text-gray-900">Custom Duration</p>
                      <p class="text-sm text-gray-500">Set your own duration</p>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Preset Duration Selection -->
              <div v-if="form.shortLeave.durationType === 'preset'">
                <select
                  v-model="form.shortLeave.duration"
                  required
                  class="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                >
                  <option value="">Select duration</option>
                  <option value="15">15 minutes</option>
                  <option value="30">30 minutes</option>
                  <option value="45">45 minutes</option>
                  <option value="60">1 hour</option>
                  <option value="90">1 hour 30 minutes</option>
                  <option value="120">2 hours</option>
                  <option value="180">3 hours</option>
                  <option value="240">4 hours</option>
                  <option value="300">5 hours</option>
                  <option value="360">6 hours</option>
                  <option value="420">7 hours</option>
                  <option value="480">8 hours</option>
                </select>
              </div>

              <!-- Custom Duration Input -->
              <div
                v-if="form.shortLeave.durationType === 'custom'"
                class="space-y-3"
              >
                <div class="grid grid-cols-2 gap-4">
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">
                      Hours
                    </label>
                    <input
                      v-model.number="form.shortLeave.customHours"
                      type="number"
                      min="0"
                      max="23"
                      placeholder="0"
                      @input="calculateCustomDuration"
                      class="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label class="block text-sm font-medium text-gray-700 mb-1">
                      Minutes
                    </label>
                    <select
                      v-model.number="form.shortLeave.customMinutes"
                      @change="calculateCustomDuration"
                      class="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    >
                      <option value="0">0</option>
                      <option value="15">15</option>
                      <option value="30">30</option>
                      <option value="45">45</option>
                    </select>
                  </div>
                </div>

                <!-- Custom Duration Display -->
                <div
                  v-if="customDurationDisplay"
                  class="bg-blue-50 border border-blue-200 rounded-md p-3"
                >
                  <p class="text-sm text-blue-800">
                    <strong>Total Duration:</strong>
                    {{ customDurationDisplay }} ({{
                      form.shortLeave.duration
                    }}
                    minutes)
                  </p>
                </div>

                <!-- Duration Validation -->
                <div
                  v-if="customDurationError"
                  class="bg-red-50 border border-red-200 rounded-md p-3"
                >
                  <p class="text-sm text-red-800">
                    {{ customDurationError }}
                  </p>
                </div>
              </div>
            </div>

            <!-- End Time Display (calculated automatically) -->
            <div
              v-if="calculatedEndTime"
              class="bg-gray-50 border border-gray-200 rounded-md p-3"
            >
              <p class="text-sm text-gray-700">
                <strong>Calculated End Time:</strong> {{ calculatedEndTime }}
              </p>
            </div>
          </div>

          <!-- Regular Leave Fields -->
          <div v-else class="space-y-4">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  Start Date *
                </label>
                <input
                  v-model="form.startDate"
                  type="date"
                  required
                  :min="getMinDate()"
                  @change="validateDates"
                  class="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  End Date *
                </label>
                <input
                  v-model="form.endDate"
                  type="date"
                  required
                  :min="form.startDate || getMinDate()"
                  @change="calculateDays"
                  class="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
            </div>

            <div
              v-if="calculatedDays > 0"
              class="bg-blue-50 border border-blue-200 rounded-md p-3"
            >
              <p class="text-sm text-blue-800">
                <strong>Duration:</strong> {{ calculatedDays }}
                {{ calculatedDays === 1 ? 'day' : 'days' }}
              </p>
              <p
                v-if="selectedLeaveTypeInfo?.balance !== undefined"
                class="text-sm text-blue-700 mt-1"
              >
                Remaining balance after this request:
                {{ selectedLeaveTypeInfo.balance - calculatedDays }} days
              </p>
            </div>
          </div>

          <!-- Reason -->
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">
              Reason *
            </label>
            <textarea
              v-model="form.reason"
              rows="4"
              required
              maxlength="500"
              placeholder="Please provide a reason for your leave request..."
              class="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
            ></textarea>
            <p class="mt-1 text-sm text-gray-500">
              {{ form.reason.length }}/500 characters
            </p>
          </div>

          <!-- File Upload Section -->
          <div class="space-y-4">
            <div class="flex items-center justify-between">
              <label class="block text-sm font-medium text-gray-700">
                Supporting Documents
                <span v-if="selectedLeaveTypeInfo?.requiresDocument" class="text-red-500"
                  >*</span
                >
                <span v-else class="text-gray-500">(Optional)</span>
              </label>
              <span class="text-xs text-gray-500">
                {{ uploadedFiles.length }}/{{ maxFiles }} files
              </span>
            </div>

            <!-- Upload Area -->
            <div
              @dragover.prevent="handleDragOver"
              @dragleave.prevent="handleDragLeave"
              @drop.prevent="handleDrop"
              :class="[
                'relative border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors',
                isDragOver
                  ? 'border-blue-400 bg-blue-50'
                  : 'border-gray-300 hover:border-gray-400',
              ]"
            >
              <input
                ref="fileInput"
                type="file"
                multiple
                :accept="acceptedFileTypes"
                @change="handleFileUpload"
                class="hidden"
              />
              <div class="space-y-2" @click="triggerFileUpload">
                <Upload class="mx-auto h-8 w-8 text-gray-400" />
                <div class="text-sm text-gray-600">
                  <span class="font-medium text-blue-600">Click to upload</span>
                  or drag and drop files here
                </div>
                <p class="text-xs text-gray-500">
                  PDF, DOC, DOCX, JPG, PNG up to {{ maxFileSize }}MB each
                </p>
              </div>
            </div>

            <!-- File Type Suggestions -->
            <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div
                v-for="suggestion in fileSuggestions"
                :key="suggestion.type"
                @click="highlightFileType(suggestion.type)"
                class="p-3 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 cursor-pointer transition-colors"
              >
                <div class="flex items-center">
                  <component
                    :is="suggestion.icon"
                    class="w-5 h-5 text-blue-600 mr-2"
                  />
                  <div>
                    <p class="text-sm font-medium text-gray-900">
                      {{ suggestion.name }}
                    </p>
                    <p class="text-xs text-gray-500">
                      {{ suggestion.description }}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Uploaded Files List -->
            <div v-if="uploadedFiles.length > 0" class="space-y-3">
              <h4 class="text-sm font-medium text-gray-900">Uploaded Files</h4>
              <div class="space-y-2">
                <div
                  v-for="(file, index) in uploadedFiles"
                  :key="index"
                  class="flex items-center justify-between p-3 bg-gray-50 border border-gray-200 rounded-lg"
                >
                  <div class="flex items-center space-x-3">
                    <div class="flex-shrink-0">
                      <component
                        :is="getFileIcon(file)"
                        class="w-8 h-8 text-gray-600"
                      />
                    </div>
                    <div class="flex-1 min-w-0">
                      <p class="text-sm font-medium text-gray-900 truncate">
                        {{ file.name }}
                      </p>
                      <div
                        class="flex items-center space-x-2 text-xs text-gray-500"
                      >
                        <span>{{ formatFileSize(file.size) }}</span>
                        <span>•</span>
                        <span>{{ getFileType(file.type) }}</span>
                        <span v-if="file.uploadProgress !== undefined">
                          • {{ file.uploadProgress }}%
                        </span>
                      </div>
                    </div>
                  </div>
                  <div class="flex items-center space-x-2">
                    <!-- Preview Button -->
                    <button
                      v-if="isPreviewable(file)"
                      type="button"
                      @click="previewFile(file, index)"
                      class="p-1 text-blue-600 hover:text-blue-800"
                      title="Preview"
                    >
                      <Eye class="w-4 h-4" />
                    </button>
                    <!-- Remove Button -->
                    <button
                      type="button"
                      @click="removeFile(index)"
                      class="p-1 text-red-600 hover:text-red-800"
                      title="Remove"
                    >
                      <Trash2 class="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- File Upload Progress -->
            <div
              v-if="isUploading"
              class="bg-blue-50 border border-blue-200 rounded-md p-3"
            >
              <div class="flex items-center">
                <Loader class="w-5 h-5 text-blue-600 animate-spin mr-2" />
                <span class="text-sm text-blue-800">Uploading files...</span>
              </div>
            </div>
          </div>

          <!-- Emergency Contact (for longer leaves) -->
          <div
            v-if="calculatedDays > 3 && !selectedLeaveTypeInfo?.isShortLeave"
            class="space-y-4"
          >
            <h4 class="font-medium text-gray-900">
              Emergency Contact Information
            </h4>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  Contact Name
                </label>
                <input
                  v-model="form.emergencyContact.name"
                  type="text"
                  class="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-2">
                  Contact Phone
                </label>
                <input
                  v-model="form.emergencyContact.phone"
                  type="tel"
                  class="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          <!-- Overlap Check Messages -->
          <div
            v-if="overlapErrors.length > 0"
            class="bg-red-50 border border-red-200 rounded-md p-3"
          >
            <div class="flex">
              <AlertCircle class="w-5 h-5 text-red-400 mt-0.5 mr-2" />
              <div>
                <h4 class="text-sm font-medium text-red-800">
                  Leave Request Conflicts:
                </h4>
                <ul class="mt-1 text-sm text-red-700 list-disc list-inside">
                  <li v-for="error in overlapErrors" :key="error.id">
                    {{ error.message }}
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <!-- Overlap Checking Status -->
          <div
            v-if="checkingOverlap"
            class="bg-blue-50 border border-blue-200 rounded-md p-3"
          >
            <div class="flex items-center">
              <Loader class="w-5 h-5 text-blue-600 animate-spin mr-2" />
              <span class="text-sm text-blue-800">Checking for conflicts...</span>
            </div>
          </div>

          <!-- Validation Messages -->
          <div
            v-if="validationErrors.length > 0"
            class="bg-red-50 border border-red-200 rounded-md p-3"
          >
            <div class="flex">
              <AlertCircle class="w-5 h-5 text-red-400 mt-0.5 mr-2" />
              <div>
                <h4 class="text-sm font-medium text-red-800">
                  Please fix the following errors:
                </h4>
                <ul class="mt-1 text-sm text-red-700 list-disc list-inside">
                  <li v-for="error in validationErrors" :key="error">
                    {{ error }}
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <!-- Actions -->
          <div class="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              @click="closeModal"
              class="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              :disabled="isSubmitting || !isFormValid"
              class="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              :class="{ 'bg-gray-400 hover:bg-gray-400': isSubmitting }"
            >
              <Loader v-if="isSubmitting" class="w-4 h-4 mr-2 animate-spin" />
              {{ isSubmitting ? 'Submitting... Please wait' : 'Submit Request' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- File Preview Modal -->
    <div
      v-if="previewModal.show"
      class="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-60"
      @click="closePreview"
    >
      <div class="relative max-w-4xl max-h-screen bg-white rounded-lg">
        <div class="flex items-center justify-between p-4 border-b">
          <h3 class="text-lg font-medium">{{ previewModal.file?.name }}</h3>
          <button
            @click="closePreview"
            class="text-gray-400 hover:text-gray-600"
          >
            <X class="w-6 h-6" />
          </button>
        </div>
        <div class="p-4">
          <img
            v-if="previewModal.type === 'image'"
            :src="previewModal.url"
            class="max-w-full max-h-96 object-contain"
            alt="Preview"
          />
          <iframe
            v-else-if="previewModal.type === 'pdf'"
            :src="previewModal.url"
            class="w-full h-96"
          ></iframe>
          <div v-else class="text-center py-8 text-gray-500">
            Preview not available for this file type
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, watch } from 'vue';
import {
  X,
  Calendar,
  Heart,
  User,
  Clock,
  FileText,
  AlertCircle,
  Loader,
  Upload,
  Eye,
  Trash2,
  Image,
  File,
  Download,
} from 'lucide-vue-next';

// Props
const props = defineProps({
  leaveTypes: {
    type: Array,
    default: () => [],
  },
});

// Emits
const emit = defineEmits(['close', 'submit']);

// Reactive data
const form = reactive({
  leaveType: '',
  startDate: '',
  endDate: '',
  reason: '',
  shortLeave: {
    date: '',
    startTime: '',
    duration: '',
    durationType: 'preset',
    customHours: 0,
    customMinutes: 0,
  },
  emergencyContact: {
    name: '',
    phone: '',
  },
});

const uploadedFiles = ref([]);
const isSubmitting = ref(false);
const isUploading = ref(false);
const calculatedDays = ref(0);
const validationErrors = ref([]);
const overlapErrors = ref([]);
const isDragOver = ref(false);
const fileInput = ref(null);
const checkingOverlap = ref(false);
const lastSubmitTime = ref(0);
const submitDebounceMs = 3000; // Prevent duplicate submissions within 3 seconds

// File upload configuration
const maxFiles = ref(10);
const maxFileSize = ref(10); // MB
const acceptedFileTypes = ref('.pdf,.doc,.docx,.jpg,.jpeg,.png,.txt,.rtf');

// Preview modal
const previewModal = reactive({
  show: false,
  file: null,
  url: '',
  type: '',
});

// File type suggestions
const fileSuggestions = ref([
  {
    type: 'medical',
    name: 'Medical Certificate',
    description: 'For sick leave requests',
    icon: Heart,
  },
  {
    type: 'document',
    name: 'Official Document',
    description: 'Government or legal papers',
    icon: FileText,
  },
  {
    type: 'proof',
    name: 'Supporting Evidence',
    description: 'Photos or documentation',
    icon: Image,
  },
]);

// Computed properties
const availableLeaveTypes = computed(() => {
  if (props.leaveTypes && props.leaveTypes.length > 0) {
    // Map database leave types to expected format. Short leave is now requested
    // and approved in advance like any other type, then consumed via Leave In/Out.
    return props.leaveTypes
      .map(type => ({
      key: type.code?.toLowerCase() || type.name?.toLowerCase().replace(/\s+/g, '_'),
      name: type.name,
      description: type.description || `${type.name} - ${type.allocated || type.maxPerYear || 0} ${type.unit || 'days'} per year`,
      balance: type.remaining || type.balance || 0,
      allocated: type.allocated || type.maxPerYear || 0,
      used: type.used || 0,
      color: type.color || '#3498db',
      id: type.id,
      maxPerYear: type.maxPerYear || type.allocated,
      requiresDocument: ['SICK', 'SL', 'sick'].includes(type.code), // Sick leave requires documents
      unit: type.unit || 'days',
      isShortLeave: ['SHORT', 'SH', 'short', 'short_leave'].includes(type.code?.toLowerCase()),
      advanceDaysRequired: type.advanceDaysRequired || 0,
    }));
  }

  // Fallback to default types if no API data
  return [
    {
      key: 'annual',
      name: 'Annual Leave',
      description: 'Planned vacation or personal time off',
      balance: 15,
      color: '#4CAF50',
    },
    {
      key: 'sick',
      name: 'Sick Leave',
      description: 'Medical reasons or health-related absence',
      balance: 10,
      color: '#FF9800',
      requiresDocument: true,
    },
    {
      key: 'personal',
      name: 'Personal Leave',
      description: 'Personal matters or family emergencies',
      balance: 5,
      color: '#2196F3',
    },
  ];
});

const selectedLeaveTypeInfo = computed(() => {
  return availableLeaveTypes.value.find(type => type.key === form.leaveType);
});

const customDurationDisplay = computed(() => {
  if (
    form.shortLeave.durationType === 'custom' &&
    form.shortLeave.duration > 0
  ) {
    const hours = Math.floor(form.shortLeave.duration / 60);
    const minutes = form.shortLeave.duration % 60;

    if (hours > 0 && minutes > 0) {
      return `${hours} hour${hours > 1 ? 's' : ''} ${minutes} minute${
        minutes > 1 ? 's' : ''
      }`;
    } else if (hours > 0) {
      return `${hours} hour${hours > 1 ? 's' : ''}`;
    } else if (minutes > 0) {
      return `${minutes} minute${minutes > 1 ? 's' : ''}`;
    }
  }
  return '';
});

const customDurationError = computed(() => {
  if (form.shortLeave.durationType === 'custom') {
    const totalMinutes = form.shortLeave.duration;

    if (totalMinutes <= 0) {
      return 'Duration must be greater than 0 minutes';
    }

    if (totalMinutes > 24 * 60) {
      return 'Duration cannot exceed 24 hours';
    }
  }
  return '';
});

const calculatedEndTime = computed(() => {
  if (form.shortLeave.startTime && form.shortLeave.duration > 0) {
    const [hours, minutes] = form.shortLeave.startTime.split(':').map(Number);
    const startMinutes = hours * 60 + minutes;
    const endMinutes = startMinutes + parseInt(form.shortLeave.duration);

    const endHours = Math.floor(endMinutes / 60) % 24;
    const endMins = endMinutes % 60;

    return `${endHours.toString().padStart(2, '0')}:${endMins
      .toString()
      .padStart(2, '0')}`;
  }
  return '';
});

const isFormValid = computed(() => {
  if (!form.leaveType || !form.reason.trim()) return false;

  // Check for overlaps
  if (overlapErrors.value.length > 0) return false;

  // Check for required files for leave types that require documents
  if (selectedLeaveTypeInfo.value?.requiresDocument && uploadedFiles.value.length === 0)
    return false;

  if (selectedLeaveTypeInfo.value?.isShortLeave || form.leaveType === 'short' || form.leaveType === 'short_leave') {
    const hasValidDuration =
      form.shortLeave.durationType === 'preset'
        ? form.shortLeave.duration
        : form.shortLeave.duration > 0 && !customDurationError.value;

    return (
      form.shortLeave.date && form.shortLeave.startTime && hasValidDuration
    );
  } else {
    return form.startDate && form.endDate && calculatedDays.value > 0;
  }
});

// Leave type styling methods
const getLeaveTypeIcon = (key) => {
  const iconMap = {
    'annual': Calendar,
    'sick': Heart,
    'personal': User,
    'short': Clock,
    'maternity': Heart,
    'paternity': User,
    'emergency': AlertCircle,
    'study': FileText,
  };
  return iconMap[key] || FileText;
};

const getLeaveTypeBackgroundClass = (type) => {
  if (type.color) {
    // Generate light background based on color
    const color = type.color.replace('#', '');
    return `bg-gray-100`; // Fallback to gray, you could enhance this to generate actual colors
  }
  
  // Fallback based on key
  const classMap = {
    'annual': 'bg-green-100',
    'sick': 'bg-red-100',
    'personal': 'bg-blue-100',
    'short': 'bg-yellow-100',
    'maternity': 'bg-pink-100',
    'paternity': 'bg-purple-100',
    'emergency': 'bg-red-100',
    'study': 'bg-teal-100',
  };
  return classMap[type.key] || 'bg-gray-100';
};

const getLeaveTypeIconClass = (type) => {
  if (type.color) {
    return 'text-gray-700'; // You could enhance this to use the actual color
  }
  
  // Fallback based on key
  const classMap = {
    'annual': 'text-green-600',
    'sick': 'text-red-600',
    'personal': 'text-blue-600',
    'short': 'text-yellow-600',
    'maternity': 'text-pink-600',
    'paternity': 'text-purple-600',
    'emergency': 'text-red-600',
    'study': 'text-teal-600',
  };
  return classMap[type.key] || 'text-gray-600';
};

// File handling methods
const triggerFileUpload = () => {
  if (fileInput.value) {
    fileInput.value.click();
  }
};

const handleFileUpload = event => {
  const files = Array.from(event.target.files || []);
  if (files.length > 0) {
    processFiles(files);
  }
  // Reset the input to allow selecting the same file again
  event.target.value = '';
};

const handleDragOver = event => {
  event.preventDefault();
  isDragOver.value = true;
};

const handleDragLeave = event => {
  event.preventDefault();
  isDragOver.value = false;
};

const handleDrop = event => {
  event.preventDefault();
  isDragOver.value = false;

  const files = Array.from(event.dataTransfer.files);
  processFiles(files);
};

const processFiles = files => {
  if (uploadedFiles.value.length + files.length > maxFiles.value) {
    validationErrors.value.push(`Maximum ${maxFiles.value} files allowed`);
    return;
  }

  files.forEach(file => {
    if (validateFile(file)) {
      uploadedFiles.value.push(file);
    }
  });

  validateForm();
};

const validateFile = file => {
  // Check file size
  if (file.size > maxFileSize.value * 1024 * 1024) {
    validationErrors.value.push(
      `File "${file.name}" is too large. Maximum size is ${maxFileSize.value}MB`
    );
    return false;
  }

  // Check file type
  const allowedTypes = [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'image/jpeg',
    'image/jpg',
    'image/png',
    'text/plain',
    'application/rtf',
  ];

  if (!allowedTypes.includes(file.type)) {
    validationErrors.value.push(
      `File "${file.name}" has an unsupported format`
    );
    return false;
  }

  return true;
};

const removeFile = index => {
  uploadedFiles.value.splice(index, 1);
  validateForm();
};

const formatFileSize = bytes => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const getFileType = mimeType => {
  const types = {
    'application/pdf': 'PDF',
    'application/msword': 'DOC',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
      'DOCX',
    'image/jpeg': 'JPG',
    'image/jpg': 'JPG',
    'image/png': 'PNG',
    'text/plain': 'TXT',
    'application/rtf': 'RTF',
  };
  return types[mimeType] || 'Unknown';
};

const getFileIcon = file => {
  if (file.type.startsWith('image/')) {
    return Image;
  } else if (file.type === 'application/pdf') {
    return FileText;
  } else {
    return File;
  }
};

const isPreviewable = file => {
  return file.type.startsWith('image/') || file.type === 'application/pdf';
};

const previewFile = (file, index) => {
  if (!isPreviewable(file)) return;

  const url = URL.createObjectURL(file);
  previewModal.show = true;
  previewModal.file = file;
  previewModal.url = url;
  previewModal.type = file.type.startsWith('image/') ? 'image' : 'pdf';
};

const closePreview = () => {
  if (previewModal.url) {
    URL.revokeObjectURL(previewModal.url);
  }
  previewModal.show = false;
  previewModal.file = null;
  previewModal.url = '';
  previewModal.type = '';
};

const highlightFileType = type => {
  // Add visual feedback for file type suggestion
  console.log(`Suggested file type: ${type}`);
};

// Form methods
const selectLeaveType = type => {
  form.leaveType = type;
  resetFormFields();
  validateForm();
};

const resetFormFields = () => {
  const isShort = selectedLeaveTypeInfo.value?.isShortLeave || form.leaveType === 'short' || form.leaveType === 'short_leave';
  
  if (!isShort) {
    form.shortLeave = {
      date: '',
      startTime: '',
      duration: '',
      durationType: 'preset',
      customHours: 0,
      customMinutes: 0,
    };
  } else {
    form.startDate = '';
    form.endDate = '';
    calculatedDays.value = 0;
  }
};

const calculateCustomDuration = () => {
  if (form.shortLeave.durationType === 'custom') {
    const hours = parseInt(form.shortLeave.customHours) || 0;
    const minutes = parseInt(form.shortLeave.customMinutes) || 0;
    form.shortLeave.duration = hours * 60 + minutes;
  }
  validateForm();
};

const validateDates = () => {
  if (form.startDate && form.endDate && form.startDate > form.endDate) {
    form.endDate = form.startDate;
  }
  calculateDays();
};

const calculateDays = () => {
  if (form.startDate && form.endDate) {
    const start = new Date(form.startDate);
    const end = new Date(form.endDate);
    calculatedDays.value = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
  } else {
    calculatedDays.value = 0;
  }
  validateForm();
};

const validateForm = () => {
  validationErrors.value = [];

  if (!form.leaveType) {
    validationErrors.value.push('Please select a leave type');
  }

  if (!form.reason.trim()) {
    validationErrors.value.push('Please provide a reason for your leave');
  }

  // File validation for leave types that require documents
  if (selectedLeaveTypeInfo.value?.requiresDocument && uploadedFiles.value.length === 0) {
    const typeName = selectedLeaveTypeInfo.value?.name || 'this leave type';
    validationErrors.value.push(
      `Supporting documents are required for ${typeName}`
    );
  }

  const isShort = selectedLeaveTypeInfo.value?.isShortLeave || form.leaveType === 'short' || form.leaveType === 'short_leave';
  
  if (isShort) {
    if (!form.shortLeave.date) {
      validationErrors.value.push('Please select a date for short leave');
    }
    if (!form.shortLeave.startTime) {
      validationErrors.value.push('Please select a start time');
    }
    if (!form.shortLeave.durationType) {
      validationErrors.value.push('Please select duration type');
    }

    if (
      form.shortLeave.durationType === 'preset' &&
      !form.shortLeave.duration
    ) {
      validationErrors.value.push('Please select duration');
    }

    if (form.shortLeave.durationType === 'custom') {
      if (form.shortLeave.duration <= 0) {
        validationErrors.value.push('Please set a valid custom duration');
      }
      if (customDurationError.value) {
        validationErrors.value.push(customDurationError.value);
      }
    }
  } else {
    if (!form.startDate) {
      validationErrors.value.push('Please select a start date');
    }
    if (!form.endDate) {
      validationErrors.value.push('Please select an end date');
    }
    if (calculatedDays.value <= 0) {
      validationErrors.value.push('Invalid date range');
    }

    // Check if sufficient balance
    const typeInfo = selectedLeaveTypeInfo.value;
    if (
      typeInfo?.balance !== undefined &&
      calculatedDays.value > typeInfo.balance
    ) {
      validationErrors.value.push(
        `Insufficient leave balance. You only have ${typeInfo.balance} days remaining.`
      );
    }
  }

  // Validate uploaded files
  if (uploadedFiles.value.length > maxFiles.value) {
    validationErrors.value.push(`Maximum ${maxFiles.value} files allowed`);
  }
};

const getTodayDate = () => {
  return new Date().toISOString().split('T')[0];
};

const getMinDate = () => {
  // Check if selected leave type allows same-day requests
  const advanceDays = selectedLeaveTypeInfo.value?.advanceDaysRequired || 0;

  if (advanceDays === 0) {
    // Allow today for leave types with no advance notice (e.g., Sick, Emergency)
    return new Date().toISOString().split('T')[0];
  }

  // Otherwise, require advance notice
  const minDate = new Date();
  minDate.setDate(minDate.getDate() + advanceDays);
  return minDate.toISOString().split('T')[0];
};

const getMaxShortLeaveDate = () => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  return tomorrow.toISOString().split('T')[0];
};

const submitRequest = async () => {
  // 🔒 DUPLICATE PREVENTION: Check if already submitting
  if (isSubmitting.value) {
    console.warn('⚠️ Submission already in progress, ignoring duplicate tap');
    return;
  }

  // 🔒 DUPLICATE PREVENTION: Debounce rapid submissions (mobile tap issue fix)
  const now = Date.now();
  if (now - lastSubmitTime.value < submitDebounceMs) {
    const remainingTime = Math.ceil((submitDebounceMs - (now - lastSubmitTime.value)) / 1000);
    validationErrors.value = [`Please wait ${remainingTime} seconds before submitting again`];
    console.warn(`⚠️ Duplicate submission prevented (${remainingTime}s remaining)`);
    return;
  }

  validateForm();

  if (validationErrors.value.length > 0) {
    return;
  }

  // 🔒 Set flags IMMEDIATELY to prevent duplicate submissions
  isSubmitting.value = true;
  isUploading.value = true;
  lastSubmitTime.value = now;

  try {
    // Create FormData for file upload
    const formData = new FormData();

    let requestData;

    const isShort = selectedLeaveTypeInfo.value?.isShortLeave || form.leaveType === 'short' || form.leaveType === 'short_leave';

    // 🔒 DUPLICATE PREVENTION: Generate unique request ID for backend deduplication
    const requestId = `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;

    if (isShort) {
      requestData = {
        leave_type: selectedLeaveTypeInfo.value?.code || 'short_leave',
        start_date: form.shortLeave.date,
        end_date: form.shortLeave.date,
        start_time: form.shortLeave.startTime,
        duration_minutes: parseInt(form.shortLeave.duration),
        duration_type: form.shortLeave.durationType,
        reason: form.reason,
        is_short_leave: true,
        request_id: requestId, // 🔒 Unique ID for deduplication
      };

      if (form.shortLeave.durationType === 'custom') {
        requestData.custom_hours = form.shortLeave.customHours;
        requestData.custom_minutes = form.shortLeave.customMinutes;
      }
    } else {
      requestData = {
        // New validation schema format
        typeId: selectedLeaveTypeInfo.value?.id,
        startDate: form.startDate,
        endDate: form.endDate,
        quantity: calculatedDays.value,
        reason: form.reason,
        // Legacy format (keep for backward compatibility)
        leave_type: selectedLeaveTypeInfo.value?.code || form.leaveType,
        start_date: form.startDate,
        end_date: form.endDate,
        days: calculatedDays.value,
        is_short_leave: false,
        request_id: requestId, // 🔒 Unique ID for deduplication
      };

      if (calculatedDays.value > 3) {
        requestData.emergency_contact_name = form.emergencyContact.name;
        requestData.emergency_contact_phone = form.emergencyContact.phone;
      }
    }

    console.log('📤 Submitting leave request with ID:', requestId);

    // Append form data
    Object.keys(requestData).forEach(key => {
      formData.append(key, requestData[key]);
    });

    // Append files
    uploadedFiles.value.forEach((file, index) => {
      formData.append(`documents`, file);
      formData.append(`document_types_${index}`, getDocumentType(file));
    });

    // Add metadata about files
    formData.append('file_count', uploadedFiles.value.length.toString());

    emit('submit', formData);
  } catch (error) {
    console.error('Error submitting request:', error);
    validationErrors.value.push('Failed to submit request. Please try again.');
    // Reset flags on error to allow retry
    isSubmitting.value = false;
    isUploading.value = false;
  }
  // Note: Do NOT reset isSubmitting in finally block - let the parent component handle it
  // This prevents rapid re-submissions before the API call completes
};

const getDocumentType = file => {
  if (file.type.startsWith('image/')) {
    return 'image';
  } else if (file.type === 'application/pdf') {
    return 'pdf';
  } else if (file.type.includes('word') || file.type.includes('document')) {
    return 'document';
  } else {
    return 'other';
  }
};

// Overlap checking function
const checkForOverlaps = async () => {
  if (checkingOverlap.value) return;

  const isShort = selectedLeaveTypeInfo.value?.isShortLeave || form.leaveType === 'short' || form.leaveType === 'short_leave';

  // Only check if we have enough data
  if (isShort) {
    if (!form.shortLeave.date || !form.shortLeave.startTime || !form.shortLeave.duration) {
      overlapErrors.value = [];
      return;
    }
  } else {
    if (!form.startDate || !form.endDate) {
      overlapErrors.value = [];
      return;
    }
  }

  checkingOverlap.value = true;
  overlapErrors.value = [];

  try {
    const payload = isShort
      ? {
          isShortLeave: true,
          shortLeaveDate: form.shortLeave.date,
          shortLeaveStartTime: form.shortLeave.startTime,
          shortLeaveDurationMinutes: parseInt(form.shortLeave.duration),
        }
      : {
          isShortLeave: false,
          startDate: form.startDate,
          endDate: form.endDate,
        };

    const response = await fetch('/api/v1/employee/leaves/check-overlap', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: JSON.stringify(payload)
    });

    const result = await response.json();

    if (result.success && result.data.hasOverlaps) {
      overlapErrors.value = result.data.overlappingRequests;
    } else {
      overlapErrors.value = [];
    }
  } catch (error) {
    console.error('Error checking overlaps:', error);
    // Don't block the form if overlap check fails
    overlapErrors.value = [];
  } finally {
    checkingOverlap.value = false;
  }
};

const closeModal = () => {
  // Clean up file URLs
  uploadedFiles.value.forEach(file => {
    if (file.previewUrl) {
      URL.revokeObjectURL(file.previewUrl);
    }
  });

  if (previewModal.url) {
    URL.revokeObjectURL(previewModal.url);
  }

  emit('close');
};

// Watch for form changes to trigger validation and overlap checking
watch(() => form.reason, validateForm);

// Short leave watchers
watch(() => form.shortLeave.date, () => {
  validateForm();
  checkForOverlaps();
});
watch(() => form.shortLeave.startTime, () => {
  validateForm();
  checkForOverlaps();
});
watch(() => form.shortLeave.duration, () => {
  validateForm();
  checkForOverlaps();
});
watch(
  () => form.shortLeave.durationType,
  () => {
    form.shortLeave.duration = '';
    form.shortLeave.customHours = 0;
    form.shortLeave.customMinutes = 0;
    validateForm();
    overlapErrors.value = []; // Clear overlap errors when changing duration type
  }
);

// Regular leave watchers
watch(() => form.startDate, () => {
  validateForm();
  checkForOverlaps();
});
watch(() => form.endDate, () => {
  validateForm();
  checkForOverlaps();
});

watch(() => uploadedFiles.value.length, validateForm);
</script>

<style scoped>
/* Custom animations */
.fixed {
  animation: fadeIn 0.3s ease-out;
}

.relative.top-20 {
  animation: slideUp 0.3s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Custom focus styles */
input:focus,
textarea:focus,
select:focus {
  outline: none;
  border-color: #3b82f6;
}

/* Leave type selection hover effects */
.cursor-pointer:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.transition-colors {
  transition: all 0.2s ease-in-out;
}

/* Custom duration inputs */
input[type='number']::-webkit-outer-spin-button,
input[type='number']::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

input[type='number'] {
  -moz-appearance: textfield;
}

/* Duration type selection cards */
.duration-type-card {
  transition: all 0.2s ease-in-out;
}

.duration-type-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* File upload area */
.drag-over {
  border-color: #3b82f6;
  background-color: #dbeafe;
}

/* File list animations */
.file-item {
  transition: all 0.2s ease-in-out;
}

.file-item:hover {
  background-color: #f3f4f6;
}

/* Modal overlay */
.z-60 {
  z-index: 60;
}

/* Scrollbar styling */
.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>
